import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useRegistration } from '../../contexts/RegistrationContext';
import { getCurrentApiEndpoints } from '../../config/api';
import RegistrationWizard from './RegistrationWizard';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  padding: 2.5rem;
  text-align: center;
`;

const Title = styled.h1`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  border-radius: 25px;
  border: none;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: #000;
    color: white;
    
    &:hover {
      background: #333;
      transform: translateY(-1px);
    }
  ` : `
    background: #f8f9fa;
    color: #000;
    border: 2px solid #000;
    
    &:hover {
      background: #e9ecef;
      transform: translateY(-1px);
    }
  `}
`;

const ExistingRegistrationInfo = styled.div`
  background: #f8f9fa;
  border: 2px solid #000;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const InfoTitle = styled.h3`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.div`
  font-size: 0.9rem;
  color: #000;
  font-weight: 500;
`;

const InfoValue = styled.div`
  color: #000;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: #000;
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
`;

interface ExistingRegistration {
  id: number;
  status: string;
  company_name_armenian?: string;
  company_name_english?: string;
  business_activity?: string;
  progress_percentage: number;
  created_at: string;
}

const RegistrationStart: React.FC = () => {
  const [existingRegistration, setExistingRegistration] = useState<ExistingRegistration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const { loadRegistration, createNewRegistration } = useRegistration();

  useEffect(() => {
    const checkExistingRegistration = async () => {
      try {
        const response = await axios.get(getCurrentApiEndpoints().REGISTRATION);
        const registration = response.data;
        
        // Check if there's meaningful data in the registration
        const hasData = registration.business_activity_code || 
                       registration.company_name_armenian || 
                       registration.owners_data ||
                       registration.decision_making ||
                       registration.additional_services;
        
        if (hasData && registration.status === 'draft') {
          setExistingRegistration({
            id: registration.id,
            status: registration.status,
            company_name_armenian: registration.company_name_armenian,
            company_name_english: registration.company_name_english,
            business_activity: registration.business_activity,
            progress_percentage: calculateProgress(registration),
            created_at: registration.created_at,
          });
        }
      } catch (error: any) {
        // If no draft registration exists (404), that's fine - just show the start new option
        if (error.response?.status === 404) {
          console.log('No existing draft registration found');
        } else {
          console.error('Failed to check existing registration:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingRegistration();
  }, []);

  const calculateProgress = (registration: any): number => {
    let completedSteps = 0;
    const totalSteps = 6;

    if (registration.business_activity_code && registration.business_description) completedSteps++;
    if (registration.company_name_armenian) completedSteps++;
    if (registration.owners_data) completedSteps++;
    if (registration.decision_making && registration.charter_preferences) completedSteps++;
    if (registration.additional_services && registration.preferred_communication) completedSteps++;
    if (registration.status === 'submitted') completedSteps++;

    return Math.round((completedSteps / totalSteps) * 100);
  };

  const handleContinueExisting = async () => {
    await loadRegistration();
    setShowWizard(true);
  };

  const handleStartNew = async () => {
    await createNewRegistration();
    setShowWizard(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Container>
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading...
          </div>
        </Card>
      </Container>
    );
  }

  if (showWizard) {
    return <RegistrationWizard />;
  }

  return (
    <Container>
      <Card>
        <Title>Company Registration</Title>
        <Description>
          Start your company registration process or continue with an existing draft.
        </Description>

        {existingRegistration ? (
          <>
            <ExistingRegistrationInfo>
              <InfoTitle>Existing Draft Registration Found</InfoTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Company Name:</InfoLabel>
                  <InfoValue>
                    {existingRegistration.company_name_armenian || 
                     existingRegistration.company_name_english || 
                     'Not specified'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Business Activity:</InfoLabel>
                  <InfoValue>
                    {existingRegistration.business_activity || 'Not specified'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Created:</InfoLabel>
                  <InfoValue>{formatDate(existingRegistration.created_at)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Progress:</InfoLabel>
                  <InfoValue>{existingRegistration.progress_percentage}% Complete</InfoValue>
                </InfoItem>
              </InfoGrid>
              <ProgressBar>
                <ProgressFill percentage={existingRegistration.progress_percentage} />
              </ProgressBar>
              <ProgressText>
                {existingRegistration.progress_percentage}% of registration completed
              </ProgressText>
            </ExistingRegistrationInfo>

            <ButtonGroup>
              <Button variant="primary" onClick={handleContinueExisting}>
                Continue Existing Registration
              </Button>
              <Button variant="secondary" onClick={handleStartNew}>
                Start New Registration
              </Button>
            </ButtonGroup>
          </>
        ) : (
          <ButtonGroup>
            <Button variant="primary" onClick={handleStartNew}>
              Start New Registration
            </Button>
          </ButtonGroup>
        )}
      </Card>
    </Container>
  );
};

export default RegistrationStart;
